import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

export default class extends Controller {
  static targets = ['messages', 'input', 'typingIndicator', 'cannedSelect', 'sendButton', 'attachmentPreview', 'attachmentInput']

  pendingAttachments = []

  static values = {
    sessionId: String,
    sessionStatus: String
  }

  connect() {
    this.subscribeToChannel()
    this.scrollToBottom()
    this.focusInput()
  }

  disconnect() {
    this.unsubscribeFromChannel()
  }

  subscribeToChannel() {
    if (!this.sessionIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: 'SupportChatChannel', session_id: this.sessionIdValue },
      {
        received: (data) => this.handleChannelMessage(data)
      }
    )
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  handleChannelMessage(data) {
    switch (data.event) {
      case 'new_message':
        this.appendMessage(data.message)
        break
      case 'typing':
        // Only show typing indicator when user (not admin) is typing
        if (!data.is_admin) {
          if (data.typing) {
            this.showTypingIndicator(data.user_name)
          } else {
            this.hideTypingIndicator()
          }
        }
        break
      case 'session_ended':
        this.handleSessionEnded()
        break
    }
  }

  appendMessage(message) {
    if (!this.hasMessagesTarget) return

    this.hideTypingIndicator()
    const messageHtml = this.buildMessageHtml(message)
    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)
    this.scrollToBottom()
  }

  buildMessageHtml(message) {
    const isAdmin = message.user?.is_admin
    const bubbleClass = isAdmin ?
      'bg-slate-900 text-white' :
      'bg-white border-2 border-neutral-200'

    let attachmentsHtml = ''
    if (message.attachments && message.attachments.length > 0) {
      attachmentsHtml = message.attachments.map((a) => `<img src="${a.url}" alt="${a.filename}" class="max-w-xs rounded mt-2">`).join('')
    }

    return `
      <div class="flex ${isAdmin ? 'justify-end' : 'justify-start'} mb-3">
        <div class="${bubbleClass} rounded-lg px-4 py-2 max-w-[80%]">
          <div class="text-xs font-medium mb-1 opacity-75">
            ${message.user?.name || 'User'}
          </div>
          <div class="whitespace-pre-wrap">${this.escapeHtml(message.content || '')}</div>
          ${attachmentsHtml}
          <div class="text-xs opacity-50 mt-1">${this.formatTime(message.created_at)}</div>
        </div>
      </div>
    `
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }

  focusInput() {
    if (this.hasInputTarget && this.sessionStatusValue === 'active') {
      this.inputTarget.focus()
    }
  }

  showTypingIndicator(userName) {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.textContent = `${userName} is typing...`
      this.typingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.add('hidden')
    }
  }

  handleInput() {
    if (this.subscription) {
      this.subscription.perform('typing', { typing: true })

      clearTimeout(this.typingTimeout)
      this.typingTimeout = setTimeout(() => {
        this.subscription.perform('typing', { typing: false })
      }, 2000)
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  handleAttachments(event) {
    const files = Array.from(event.target.files)
    if (!files.length) return

    // Add to pending attachments (max 3 total)
    const remainingSlots = 3 - this.pendingAttachments.length
    const filesToAdd = files.slice(0, remainingSlots)

    filesToAdd.forEach((file) => {
      if (file.type.startsWith('image/')) {
        this.pendingAttachments.push(file)
        this.addAttachmentPreview(file)
      }
    })

    // Clear the file input so the same file can be selected again
    event.target.value = ''
  }

  addAttachmentPreview(file) {
    if (!this.hasAttachmentPreviewTarget) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const wrapper = document.createElement('div')
      wrapper.className = 'relative'
      wrapper.dataset.filename = file.name

      const img = document.createElement('img')
      img.src = e.target.result
      img.className = 'h-12 w-12 object-cover border-2 border-neutral-900'

      const removeBtn = document.createElement('button')
      removeBtn.type = 'button'
      removeBtn.className =
        'absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center hover:bg-red-600'
      removeBtn.innerHTML = '×'
      removeBtn.onclick = () => this.removeAttachment(file.name, wrapper)

      wrapper.appendChild(img)
      wrapper.appendChild(removeBtn)
      this.attachmentPreviewTarget.appendChild(wrapper)
    }
    reader.readAsDataURL(file)
  }

  removeAttachment(filename, wrapper) {
    this.pendingAttachments = this.pendingAttachments.filter((f) => f.name !== filename)
    wrapper.remove()
  }

  clearAttachments() {
    this.pendingAttachments = []
    if (this.hasAttachmentPreviewTarget) {
      this.attachmentPreviewTarget.innerHTML = ''
    }
  }

  insertCannedResponse() {
    if (!this.hasCannedSelectTarget || !this.hasInputTarget) return

    const selectedOption = this.cannedSelectTarget.selectedOptions[0]
    if (!selectedOption || !selectedOption.value) return

    const content = selectedOption.dataset.content
    if (content) {
      this.inputTarget.value = content
      this.inputTarget.focus()
    }

    this.cannedSelectTarget.value = ''
  }

  sendMessage(event) {
    if (event) event.preventDefault()

    if (!this.hasInputTarget) return

    const content = this.inputTarget.value.trim()
    const hasAttachments = this.pendingAttachments.length > 0

    // Need either content or attachments
    if (!content && !hasAttachments) return

    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }

    const formData = new FormData()
    formData.append('content', content)

    // Add attachments
    this.pendingAttachments.forEach((file) => {
      formData.append('attachments[]', file)
    })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(`/admin/support/chat_sessions/${this.sessionIdValue}/messages`, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.inputTarget.value = ''
          this.clearAttachments()
        } else {
          this.showNotification(data.error || 'Failed to send message', 'error')
        }
      })
      .catch(() => {
        this.showNotification('Failed to send message', 'error')
      })
      .finally(() => {
        if (this.hasSendButtonTarget) {
          this.sendButtonTarget.disabled = false
        }
        this.inputTarget.focus()
      })
  }

  handleSessionEnded() {
    this.sessionStatusValue = 'ended'
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
      this.inputTarget.placeholder = 'Chat session has ended'
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  formatTime(timestamp) {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  showNotification(message, type = 'info') {
    const colors = {
      info: 'bg-blue-50 border-blue-500 text-blue-700',
      success: 'bg-green-50 border-green-500 text-green-700',
      error: 'bg-red-50 border-red-500 text-red-700'
    }

    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded border-2 ${colors[type] || colors.info}`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 5000)
  }
}
